import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { BenefitIcon } from "@/components/BenefitIcon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ComparePlans } from "@/components/ComparePlans";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, GitCompare, X } from "lucide-react";

export default function BenefitCategory() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.error("Compare up to 3 plans at a time.");
        return prev;
      }
      return [...prev, id];
    });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/benefits/categories"), api.get(`/products?category=${key}`)])
      .then(([c, p]) => {
        setCategory(c.data.find((x) => x.key === key));
        setProducts(p.data);
      })
      .finally(() => setLoading(false));
  }, [key]);

  const money = (n) => (n == null ? "—" : `$${n.toFixed(2)}/mo`);
  const lowest = (p) => {
    const prices = (p.coverage_options || []).map((o) => o.monthly_premium).filter((x) => x != null);
    return prices.length ? Math.min(...prices) : null;
  };

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-6xl mx-auto animate-rise">
      <Link to="/app/benefits" className="inline-flex items-center gap-1 text-sm text-navy-700 hover:text-gold-dark mb-6" data-testid="back-to-benefits">
        <ArrowLeft className="w-4 h-4" /> All benefits
      </Link>

      {loading ? (
        <Skeleton className="h-24 w-full mb-8" />
      ) : (
        <div className="flex items-start gap-5 mb-10">
          <div className="w-16 h-16 rounded-2xl bg-navy-800 flex items-center justify-center shrink-0">
            <BenefitIcon name={category?.icon} className="w-8 h-8 text-gold" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-gold-dark text-xs tracking-[0.2em] uppercase font-bold">Benefit category</span>
              <DemoBadge />
            </div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-navy-900 tracking-tight">{category?.name}</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">{category?.plain_language}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-56 w-full rounded-xl" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </div>
      ) : products.length ? (
        <div className="grid md:grid-cols-2 gap-6">
          {products.map((p, i) => (
            <div key={p.id} className="bg-white rounded-xl border border-mist-200 p-7 card-lift flex flex-col animate-rise" style={{ animationDelay: `${i * 70}ms` }} data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-navy-600 uppercase tracking-wider">{p.carrier?.name}</p>
                  <h3 className="font-display text-2xl font-bold text-navy-900 mt-1">{p.name}</h3>
                  <p className="text-sm text-muted-foreground">{p.product_type}</p>
                </div>
                <DemoBadge />
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit" data-testid={`compare-toggle-${p.id}`}>
                <Checkbox checked={compareIds.includes(p.id)} onCheckedChange={() => toggleCompare(p.id)} />
                <span className="text-xs font-semibold text-navy-700 flex items-center gap-1"><GitCompare className="w-3.5 h-3.5" /> Compare</span>
              </label>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">{p.plain_language || p.summary}</p>
              {p.riders?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {p.riders.map((r) => (
                    <span key={r} className="inline-flex items-center gap-1 text-xs bg-mist-100 text-navy-700 px-2.5 py-1 rounded-full">
                      <Check className="w-3 h-3 text-gold-dark" /> {r}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-mist-200">
                <div>
                  <p className="text-xs text-muted-foreground">Starting at</p>
                  <p className="font-display text-xl font-bold text-navy-900">{money(lowest(p))}</p>
                </div>
                <Button onClick={() => navigate(`/app/product/${p.id}`)} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid={`view-product-${p.id}`}>
                  View details <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center" data-testid="no-products">
          <p className="font-semibold text-navy-900">No options available yet</p>
          <p className="text-sm text-muted-foreground mt-1">Your employer hasn't offered products in this category, or the catalog is awaiting real carrier data.</p>
        </div>
      )}

      {/* Sticky compare bar */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-navy-800 text-white rounded-full shadow-2xl px-4 py-3 flex items-center gap-4 animate-rise" data-testid="compare-bar">
          <span className="text-sm font-semibold pl-2">{compareIds.length} plan{compareIds.length > 1 ? "s" : ""} selected</span>
          <Button onClick={() => setShowCompare(true)} disabled={compareIds.length < 2} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" size="sm" data-testid="open-compare">
            <GitCompare className="w-4 h-4 mr-1" /> Compare
          </Button>
          <button onClick={() => setCompareIds([])} className="text-mist-300 hover:text-white" data-testid="clear-compare">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="bg-white max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Compare {category?.name} options</DialogTitle>
            <DialogDescription>Weigh your selected plans side by side, then elect the one that fits your household.</DialogDescription>
          </DialogHeader>
          <ComparePlans products={products.filter((p) => compareIds.includes(p.id))} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
